import { CollisionRule, HandicapSettings } from "../types.ts";

/**
 * Sdílené výpočty a pomocné funkce pro turnajovou tabulku.
 *
 * Funkce pracují nad "hodnotovou" maticí výsledků ((number | null)[][]),
 * takže je lze použít jak pro rozehraný turnaj (Result[][]), tak pro
 * turnaj načtený z archivu (uložený jako JSON).
 */

export type ResultMatrix = (number | null)[][];

/** Součet bodů jednotlivých hráčů (mimo diagonálu). */
export const calculatePoints = (
  matrix: ResultMatrix,
  playerCount: number
): number[] => {
  if (!matrix || matrix.length === 0) {
    return Array(playerCount).fill(0);
  }

  const points: number[] = [];

  for (let i = 0; i < playerCount; i++) {
    let total = 0;
    if (matrix[i]) {
      for (let j = 0; j < playerCount; j++) {
        if (i !== j && matrix[i][j] !== null && matrix[i][j] !== undefined) {
          total += matrix[i][j] as number;
        }
      }
    }
    points.push(total);
  }

  return points;
};

/** Pomocné hodnocení PH1 – součet bodů všech soupeřů. */
export const calculateBuchholz = (
  matrix: ResultMatrix,
  playerCount: number
): number[] => {
  const playerPoints = calculatePoints(matrix, playerCount);
  const buchholzScores: number[] = [];

  for (let i = 0; i < playerCount; i++) {
    let buchholzScore = 0;
    for (let j = 0; j < playerCount; j++) {
      if (i !== j) {
        buchholzScore += playerPoints[j];
      }
    }
    buchholzScores.push(buchholzScore);
  }

  return buchholzScores;
};

/** Pomocné hodnocení PH2 – Sonneborn-Berger. */
export const calculateSonnebornBerger = (
  matrix: ResultMatrix,
  playerCount: number
): number[] => {
  const playerPoints = calculatePoints(matrix, playerCount);
  const sonnebornScores: number[] = [];

  for (let i = 0; i < playerCount; i++) {
    let sonnebornScore = 0;

    for (let j = 0; j < playerCount; j++) {
      if (i === j) continue;
      const value = matrix[i]?.[j];
      if (value !== null && value !== undefined) {
        // Násobení, ne větvení na výhru/remízu: u dvoukolového turnaje
        // má buňka i hodnoty 1,5 nebo 2 a ty se musí započítat celé.
        sonnebornScore += value * playerPoints[j];
      }
    }

    sonnebornScores.push(sonnebornScore);
  }

  return sonnebornScores;
};

/** Pořadí hráčů podle bodů, poté PH1 a PH2. */
export const calculateRankings = (
  matrix: ResultMatrix,
  playerCount: number
): { [key: number]: number } => {
  const points = calculatePoints(matrix, playerCount);
  const buchholzScores = calculateBuchholz(matrix, playerCount);
  const sonnebornScores = calculateSonnebornBerger(matrix, playerCount);

  const indexed = points.map((point, index) => [
    index,
    point,
    buchholzScores[index],
    sonnebornScores[index],
  ]);

  indexed.sort((a, b) => {
    if (b[1] !== a[1]) {
      return b[1] - a[1];
    }
    if (b[2] !== a[2]) {
      return b[2] - a[2];
    }
    return b[3] - a[3];
  });

  const rankings: { [key: number]: number } = {};
  indexed.forEach((item, i) => {
    rankings[item[0]] = i + 1;
  });

  return rankings;
};

/** Kontrola, zda byly odehrány všechny partie. */
export const areAllGamesPlayed = (
  matrix: ResultMatrix,
  playerCount: number
): boolean => {
  if (!matrix || matrix.length === 0) {
    return false;
  }

  for (let i = 0; i < playerCount; i++) {
    if (!matrix[i]) {
      return false;
    }

    for (let j = 0; j < playerCount; j++) {
      if (i === j) {
        continue;
      }
      if (matrix[i][j] === null || matrix[i][j] === undefined) {
        return false;
      }
    }
  }

  return true;
};

// --- Ořez tabulky na obsazená místa ---------------------------------------

/**
 * Turnaj se hraje jen mezi vybranými hráči. Tabulka může být nadimenzovaná
 * na 10 míst, ale když dorazí jen 6 lidí, počítá i ukládá se podtabulka 6×6.
 * Tyhle funkce převádějí mezi "indexem řádku v tabulce" a "pozicí v turnaji".
 */

/** Indexy řádků, na kterých je vybraný hráč. */
export const getActiveRows = (
  assigned: boolean[],
  playerCount: number
): number[] => {
  const rows: number[] = [];
  for (let i = 0; i < playerCount; i++) {
    if (assigned[i]) rows.push(i);
  }
  return rows;
};

/**
 * Výřez matice jen pro zadané řádky (a stejné sloupce).
 * Diagonála se vynuluje – nikdy nenese výsledek partie.
 */
export const cropMatrix = (
  matrix: ResultMatrix,
  rows: number[]
): ResultMatrix =>
  rows.map((row, i) =>
    rows.map((col, j) => (i === j ? null : matrix[row]?.[col] ?? null))
  );

/**
 * Rozbalí hodnoty spočítané nad výřezem zpět na indexy řádků tabulky.
 * Neobsazené řádky zůstanou `undefined`, takže se v tabulce nevypíšou.
 */
export const expandToRows = <T>(
  values: T[],
  rows: number[],
  size: number
): (T | undefined)[] => {
  const expanded: (T | undefined)[] = Array(size).fill(undefined);
  rows.forEach((row, index) => {
    expanded[row] = values[index];
  });
  return expanded;
};

// --- Dvoukolový turnaj ----------------------------------------------------

/**
 * Sečte kola do jedné matice skóre.
 *
 * Všechny výpočty (body, PH1, PH2, pořadí) pak běží beze změny nad ní:
 * body jsou prostý součet a Sonneborn-Berger je `body soupeře × skóre`,
 * takže dvě partie proti témuž soupeři se korektně sečtou.
 *
 * Buňka zůstane null jen tehdy, když není odehraná ani jedna partie.
 */
export const combineRounds = (
  first: ResultMatrix,
  second: ResultMatrix | null,
  playerCount: number
): ResultMatrix => {
  if (!second) return first;

  const combined: ResultMatrix = [];

  for (let i = 0; i < playerCount; i++) {
    combined[i] = [];
    for (let j = 0; j < playerCount; j++) {
      const a = first[i]?.[j];
      const b = second[i]?.[j];
      const hasA = a !== null && a !== undefined;
      const hasB = b !== null && b !== undefined;

      if (i === j || (!hasA && !hasB)) {
        combined[i][j] = null;
      } else {
        combined[i][j] = (hasA ? (a as number) : 0) + (hasB ? (b as number) : 0);
      }
    }
  }

  return combined;
};

/**
 * Kontrola dohranosti přes všechna kola. Nestačí se ptát součtové matice –
 * ta by neodhalila dvojici, která má odehranou jen jednu ze dvou partií.
 */
export const areAllRoundsPlayed = (
  rounds: ResultMatrix[],
  playerCount: number
): boolean =>
  rounds.length > 0 &&
  rounds.every((round) => areAllGamesPlayed(round, playerCount));

/** Opačný výsledek partie z pohledu soupeře. */
export const invertResult = (
  value: number | null | undefined
): number | null => {
  if (value === null || value === undefined) return null;
  return 1 - value;
};

/** Barevné šachovnicové schéma tabulky. */
export const generateTableColors = (size: number): string[][] => {
  const colors: string[][] = Array.from({ length: size }, () =>
    Array(size).fill("")
  );

  for (let i = 0; i < size; i++) {
    for (let j = 0; j < size; j++) {
      if (i === j) {
        colors[i][j] = "bg-gray-400"; // Hlavní diagonála
      } else if (colors[j][i]) {
        colors[i][j] =
          colors[j][i] === "bg-gray-200" ? "bg-white" : "bg-gray-200"; // Zrcadlový efekt
      } else {
        colors[i][j] = (i + j) % 2 === 0 ? "bg-gray-200" : "bg-white"; // Střídání barev
      }
    }
  }

  return colors;
};

/** Zobrazení výsledku partie. */
export const formatResult = (value: number | null | undefined): string => {
  if (value === null || value === undefined) return "";
  if (value === 0.5) return "½";
  return value.toString();
};

/**
 * Čas (handicap) je uložen jako číslo ve tvaru mm.ss – např. 5.30 = 5:30.
 * Zobrazí se jako "mm:ss", prázdná hodnota jako "---".
 */
export const formatTime = (time: number | null | undefined): string => {
  if (time === null || time === undefined || isNaN(time)) return "---";
  return time.toFixed(2).replace(".", ":");
};

/** Hodnota času pro editaci (bez zástupného "---"). */
export const timeToInput = (time: number | null | undefined): string => {
  if (time === null || time === undefined || isNaN(time) || time === 0) {
    return "";
  }
  return time.toFixed(2).replace(".", ":");
};

/**
 * Převod uživatelského vstupu ("5:30", "5.30", "5") na číslo mm.ss.
 * Vrací null, pokud vstup není platný čas.
 */
export const parseTime = (input: string): number | null => {
  const trimmed = (input || "").trim().replace(",", ":").replace(".", ":");
  if (trimmed === "") return null;

  const parts = trimmed.split(":");
  if (parts.length > 2) return null;

  const minutes = Number(parts[0]);
  if (!Number.isInteger(minutes) || minutes < 0 || minutes > 99) return null;

  let seconds = 0;
  if (parts.length === 2) {
    if (parts[1] === "") return null;
    // "5:3" chápeme jako 5:30, "5:03" jako 5:03
    const secondsText = parts[1].length === 1 ? `${parts[1]}0` : parts[1];
    seconds = Number(secondsText);
    if (!Number.isInteger(seconds) || seconds < 0 || seconds > 59) return null;
  }

  return Math.round((minutes + seconds / 100) * 100) / 100;
};

// --- Přepočet časů (handicapů) po turnaji ---------------------------------

/**
 * Čas je uložen jako číslo mm.ss. Veškerá aritmetika se proto dělá
 * v sekundách – jinak by "5:30 minus 2 minuty" dopadlo špatně.
 */
export const timeToSeconds = (
  time: number | null | undefined
): number | null => {
  if (time === null || time === undefined || isNaN(time)) return null;
  const hundredths = Math.round(time * 100);
  const minutes = Math.floor(hundredths / 100);
  const seconds = hundredths % 100;
  return minutes * 60 + seconds;
};

/** Zpět ze sekund na číslo mm.ss. */
export const secondsToTime = (seconds: number): number => {
  const total = Math.max(0, Math.round(seconds));
  const minutes = Math.floor(total / 60);
  const rest = total % 60;
  return Math.round((minutes + rest / 100) * 100) / 100;
};

/**
 * Výchozí nastavení přepočtu: 2:00 až 10:00, přednost mají stupně vítězů.
 *
 * Dvě minuty jsou spodní hranice pro automatický přepočet. Kratší čas je
 * dobrovolný – nastavit si ho může jen sám hráč ručně v tabulce.
 */
export const DEFAULT_HANDICAP_SETTINGS: HandicapSettings = {
  min: 2.0, // 2:00
  max: 10.0, // 10:00
  collisionRule: "podium",
};

/** Původní výchozí minimum, než se změnilo na 2:00. */
export const LEGACY_HANDICAP_MIN = 1.3;

export const COLLISION_RULE_LABELS: { [key in CollisionRule]: string } = {
  podium: "Přednost mají stupně vítězů (výchozí)",
  sum: "Změny se sečtou (−1 a +1 se vyruší)",
  bottom: "Přednost má spodek tabulky",
};

/**
 * Změna času v minutách podle pořadí v turnaji.
 *  1. místo             −2 min
 *  2. a 3. místo        −1 min
 *  poslední tři místa   +1 min
 *
 * U malých turnajů (5 a méně hráčů) může na jednoho hráče sednout obojí –
 * o výsledku pak rozhoduje `rule`.
 *
 * @param rank  pořadí hráče (1 = první)
 * @param count počet hodnocených hráčů
 */
export const getHandicapDelta = (
  rank: number,
  count: number,
  rule: CollisionRule = "podium"
): number => {
  if (rank < 1 || count < 1) return 0;

  const podium = rank === 1 ? -2 : rank === 2 || rank === 3 ? -1 : 0;
  const bottom = rank >= count - 2 ? 1 : 0;

  if (podium !== 0 && bottom !== 0) {
    if (rule === "podium") return podium;
    if (rule === "bottom") return bottom;
    return podium + bottom; // "sum"
  }

  return podium + bottom;
};

/**
 * Nový čas hráče. Beze změny (delta 0) zůstává čas přesně takový, jaký byl –
 * meze se uplatní jen na hráče, kterým se čas mění.
 *
 * Výjimka: hráč sedící přesně na minimu (1:30) je ve skutečnosti "půl kroku"
 * pod celou minutou – minimum je jen dno, na které spadl. Počítá se proto,
 * jako by měl 1:00, takže se vrací na celé minuty:
 *   1:30 + 1 min = 2:00   (ne 2:30)
 *   1:30 + 2 min = 3:00   (ne 3:30)
 * Při snižování je výsledek stejný tak i tak – dno stejně ořízne.
 */
export const calculateNewTime = (
  currentTime: number | null | undefined,
  deltaMinutes: number,
  settings: HandicapSettings = DEFAULT_HANDICAP_SETTINGS
): number | null => {
  const current = timeToSeconds(currentTime);
  if (current === null) return null;
  if (deltaMinutes === 0) return secondsToTime(current);

  const minSeconds =
    timeToSeconds(settings.min) ??
    (timeToSeconds(DEFAULT_HANDICAP_SETTINGS.min) as number);
  const maxSeconds =
    timeToSeconds(settings.max) ??
    (timeToSeconds(DEFAULT_HANDICAP_SETTINGS.max) as number);

  const lower = Math.min(minSeconds, maxSeconds);
  const upper = Math.max(minSeconds, maxSeconds);

  // Hráč na minimu počítá z celé minuty pod ním (1:30 -> 1:00).
  const base =
    current === lower && lower % 60 !== 0
      ? Math.floor(lower / 60) * 60
      : current;

  let next = base + deltaMinutes * 60;
  if (next < lower) next = lower;
  if (next > upper) next = upper;

  // Bonus nesmí čas nikdy prodloužit a přirážka zkrátit. Bez toho by hráč,
  // který je dobrovolně pod minimem, dostal za výhru delší čas, protože by
  // ho meze vytáhly nahoru.
  if (deltaMinutes < 0) next = Math.min(next, current);
  if (deltaMinutes > 0) next = Math.max(next, current);

  return secondsToTime(next);
};

/**
 * Nové časy pro celou tabulku turnaje.
 * Pořadí se určuje jen mezi řádky s vybraným hráčem – prázdné řádky
 * se do "posledního" a "předposledního" místa nepočítají.
 *
 * @param currentTimes časy, se kterými hráči hráli (index = řádek tabulky)
 * @param assigned     příznak, zda je na řádku vybraný hráč
 * @param rankings     pořadí z calculateRankings
 */
export const calculateNewTimes = (
  currentTimes: (number | null)[],
  assigned: boolean[],
  rankings: { [key: number]: number },
  playerCount: number,
  settings: HandicapSettings = DEFAULT_HANDICAP_SETTINGS
): (number | null)[] => {
  const newTimes: (number | null)[] = Array(playerCount).fill(null);

  const rows: number[] = [];
  for (let i = 0; i < playerCount; i++) {
    if (assigned[i]) rows.push(i);
  }

  rows.sort((a, b) => {
    const rankA = rankings[a] ?? Number.MAX_SAFE_INTEGER;
    const rankB = rankings[b] ?? Number.MAX_SAFE_INTEGER;
    if (rankA !== rankB) return rankA - rankB;
    return a - b;
  });

  rows.forEach((row, index) => {
    const delta = getHandicapDelta(index + 1, rows.length, settings.collisionRule);
    newTimes[row] = calculateNewTime(currentTimes[row], delta, settings);
  });

  return newTimes;
};
